import { useCallback, useEffect, useRef, useState } from 'react';
import { auditService } from '../../services/auditService';
import { useAuth } from '../../context/AuthContext';
import { fmtQty } from '../Reports/ui';
import { PageLoader } from '../../components/Loader';
import Pager, { usePaged } from '../../components/Pager';

export const DECISION = {
  approved: { label: 'Approved', tone: 'good' },
  approved_with_remarks: { label: 'Approved with remarks', tone: 'warn' },
  disputed: { label: 'Disputed', tone: 'bad' },
};

function when(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

/** Opens a printable sign-off certificate in a new window. */
function printCertificate(signoff) {
  const s = signoff.summary || {};
  const decision = DECISION[signoff.decision]?.label || signoff.decision;
  const rows = [
    ['Schedule', s.audit_name],
    ['Organization', s.organization],
    ['Outlet', [s.outlet, s.outlet_code].filter(Boolean).join(' · ')],
    ['Schedule period', `${when(s.start_at)} – ${when(s.end_at)}`],
    ['Valid scans / quantity', `${fmtQty(s.valid_scans)} / ${fmtQty(s.valid_qty)}`],
    ['Invalid scans', fmtQty(s.invalid_scans)],
    ['Progress', s.progress?.percent != null ? `${s.progress.percent}% (${s.progress.basis === 'book' ? `${fmtQty(s.progress.verified_qty)} of ${fmtQty(s.progress.book_qty)} book units` : `${fmtQty(s.progress.eans_scanned)} of ${fmtQty(s.progress.eans_total)} master items`})` : '—'],
    ['Locations completed', s.zones?.total_locations ? `${fmtQty(s.zones.scanned_locations)} / ${fmtQty(s.zones.total_locations)}` : '—'],
    ['Discrepancy alerts', s.book_loaded ? `${fmtQty(s.discrepancies?.flagged)} flagged · ${fmtQty(s.discrepancies?.awaiting_recount)} awaiting recount` : 'No book stock loaded'],
    ['Recount requests', s.recounts ? `${fmtQty(s.recounts.open + s.recounts.acknowledged)} open · ${fmtQty(s.recounts.done)} done` : '—'],
  ];
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Schedule sign-off</title>
<style>body{font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;margin:40px;color:#0f172a}h1{margin:0 0 4px}
.muted{color:#64748b}table{border-collapse:collapse;width:100%;margin:20px 0}td{border-bottom:1px solid #e2e8f0;padding:8px 6px;vertical-align:top}
td:first-child{color:#64748b;width:220px}.decision{display:inline-block;padding:4px 12px;border-radius:999px;font-weight:700;background:#f1f5f9}
.sig{margin-top:24px;border-top:1px solid #0f172a;padding-top:8px;width:360px}img{max-width:360px;max-height:140px;display:block}
@media print{button{display:none}}</style></head><body>
<button onclick="window.print()" style="float:right;padding:8px 14px">Print</button>
<h1>Schedule sign-off</h1><p class="muted">Signed ${escapeHtml(when(signoff.signed_at))}</p>
<p><span class="decision">${escapeHtml(decision)}</span></p>
${signoff.remarks ? `<p><strong>Remarks:</strong> ${escapeHtml(signoff.remarks)}</p>` : ''}
<table>${rows.map(([k, v]) => `<tr><td>${escapeHtml(k)}</td><td>${escapeHtml(v || '—')}</td></tr>`).join('')}</table>
<div class="sig">${signoff.signature_png ? `<img src="${escapeHtml(signoff.signature_png)}" alt="Signature">` : ''}
<strong>${escapeHtml(signoff.signer_name)}</strong>${signoff.signer_title ? `, ${escapeHtml(signoff.signer_title)}` : ''}<br>
<span class="muted">${escapeHtml(signoff.signer_email || '')}</span></div>
<p class="muted" style="margin-top:28px">Figures are a snapshot taken when this sign-off was made.</p>
</body></html>`;
  const win = window.open('', '_blank');
  if (!win) return false;
  win.document.open();
  win.document.write(html);
  win.document.close();
  return true;
}

/** Draw-with-finger / mouse signature box. onChange(dataUrl | null). */
function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const drawn = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0f172a';
  }, []);

  const point = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const start = (event) => {
    event.preventDefault();
    canvasRef.current.setPointerCapture?.(event.pointerId);
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = point(event);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (event) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = point(event);
    ctx.lineTo(x, y);
    ctx.stroke();
    drawn.current = true;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (drawn.current) onChange(canvasRef.current.toDataURL('image/png'));
  };
  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    drawn.current = false;
    onChange(null);
  };

  return (
    <div className="live-sigpad">
      <canvas
        ref={canvasRef}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
        aria-label="Signature box: draw your signature"
      />
      <button type="button" className="compare-link" onClick={clear}>
        Clear signature
      </button>
    </div>
  );
}

/** Client review and digital sign-off dialog. */
function SignoffDialog({ auditId, audit, overview, onClose, onSigned }) {
  const { user } = useAuth();
  const [decision, setDecision] = useState('approved');
  const [remarks, setRemarks] = useState('');
  const [name, setName] = useState(user?.name || '');
  const [title, setTitle] = useState(user?.client_title || '');
  const [signature, setSignature] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const live = audit && new Date(audit.end_at) > new Date();
  const openRecounts = (overview?.recounts?.open || 0) + (overview?.recounts?.acknowledged || 0);
  const awaiting = overview?.discrepancies?.awaiting_recount || 0;

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    if (decision !== 'approved' && !remarks.trim()) {
      setError('Add remarks explaining what needs attention.');
      return;
    }
    setSaving(true);
    try {
      const signed = await auditService.createSignoff(auditId, {
        decision,
        remarks: remarks.trim(),
        signer_name: name.trim(),
        signer_title: title.trim(),
        signature_png: signature || undefined,
        confirmed,
      });
      onSigned(signed);
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const p = overview?.progress;

  return (
    <div className="live-modal" role="dialog" aria-modal="true" aria-label="Review and sign off">
      <div className="live-modal__backdrop" onClick={saving ? undefined : onClose} />
      <form className="live-modal__panel live-modal__panel--wide" onSubmit={submit}>
        <header className="live-modal__header">
          <h2>Review &amp; digital sign-off</h2>
          <button type="button" onClick={onClose} aria-label="Close" disabled={saving}>
            ×
          </button>
        </header>

        {live || openRecounts || awaiting ? (
          <div className="live-warning">
            {live ? <p>This schedule is still running (ends {when(audit.end_at)}). Figures may still change.</p> : null}
            {openRecounts ? <p>{openRecounts} recount request{openRecounts === 1 ? ' is' : 's are'} still open.</p> : null}
            {awaiting ? <p>{awaiting} discrepanc{awaiting === 1 ? 'y is' : 'ies are'} awaiting a recount.</p> : null}
          </div>
        ) : null}

        <dl className="live-review">
          <div>
            <dt>Valid scans / quantity</dt>
            <dd>
              {fmtQty(overview?.scans?.valid_scans)} / {fmtQty(overview?.scans?.valid_qty)}
            </dd>
          </div>
          <div>
            <dt>Progress</dt>
            <dd>
              {p?.percent != null
                ? `${p.percent}% · ${p.basis === 'book' ? `${fmtQty(p.verified_qty)} of ${fmtQty(p.book_qty)} book units` : `${fmtQty(p.eans_scanned)} of ${fmtQty(p.eans_total)} items`}`
                : '—'}
            </dd>
          </div>
          <div>
            <dt>Locations completed</dt>
            <dd>
              {overview?.zones?.total_locations
                ? `${fmtQty(overview.zones.scanned_locations)} / ${fmtQty(overview.zones.total_locations)}`
                : '—'}
            </dd>
          </div>
          <div>
            <dt>Discrepancy alerts</dt>
            <dd>
              {overview?.discrepancies?.has_book
                ? `${fmtQty(overview.discrepancies.flagged)} flagged · ${fmtQty(overview.discrepancies.recounted)} recounted`
                : 'No book stock loaded'}
            </dd>
          </div>
        </dl>

        {error ? <p className="compare-error">{error}</p> : null}

        <fieldset className="live-decision">
          <legend>Your decision</legend>
          {Object.entries(DECISION).map(([value, info]) => (
            <label key={value} className={`live-decision__option live-decision__option--${info.tone}`}>
              <input type="radio" name="decision" value={value} checked={decision === value} onChange={() => setDecision(value)} />
              {info.label}
            </label>
          ))}
        </fieldset>

        <label className="live-field">
          Remarks{decision !== 'approved' ? ' *' : ' (optional)'}
          <textarea rows={3} value={remarks} onChange={(event) => setRemarks(event.target.value)} placeholder={decision === 'disputed' ? 'What do you disagree with?' : 'Anything to note for the record'} />
        </label>

        <div className="live-row">
          <label className="live-field">
            Full name *
            <input value={name} onChange={(event) => setName(event.target.value)} required />
          </label>
          <label className="live-field">
            Title
            <input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="e.g. Store Manager" />
          </label>
        </div>

        <div className="live-field">
          Signature (draw with your finger or mouse; optional)
          <SignaturePad onChange={setSignature} />
        </div>

        <label className="live-check">
          <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />
          I have reviewed this schedule's results and I sign off with the decision above.
        </label>

        <footer className="live-modal__footer">
          <button type="button" className="compare-button compare-button--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="compare-button" disabled={saving || !confirmed || !name.trim()}>
            {saving ? 'Signing…' : 'Sign off'}
          </button>
        </footer>
      </form>
    </div>
  );
}

/**
 * Sign-off tab: every client decision with its snapshot. Clients sign here
 * (or from the header button); everyone can print a certificate.
 */
function SignoffPanel({ auditId, isClient, refreshKey, onSignClick }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setRows((await auditService.getSignoffs(auditId)) || []);
      setError('');
    } catch (err) {
      setError(err.message);
    }
  }, [auditId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const current = (rows || []).filter((row) => row.is_current);
  const history = (rows || []).filter((row) => !row.is_current);
  const historyPaged = usePaged(history, { pageSize: 10, sizes: [10, 25, 50] });

  const renderRow = (row) => {
    const info = DECISION[row.decision] || { label: row.decision, tone: 'muted' };
    return (
      <li key={row.id} className={`live-signoff live-signoff--${info.tone}`}>
        <div>
          <span className={`live-pill live-pill--${info.tone}`}>{info.label}</span>{' '}
          <strong>{row.signer_name}</strong>
          {row.signer_title ? `, ${row.signer_title}` : ''}
          <p className="live-muted">
            {row.signer_email ? `${row.signer_email} · ` : ''}
            {when(row.signed_at)}
          </p>
          {row.remarks ? <p className="live-recount__note">“{row.remarks}”</p> : null}
        </div>
        <div className="live-signoff__side">
          {row.signature_png ? <img src={row.signature_png} alt={`Signature of ${row.signer_name}`} /> : null}
          <button
            type="button"
            className="compare-link"
            onClick={() => {
              if (!printCertificate(row)) setError('Allow pop-ups for this site to print the certificate.');
            }}
          >
            Print certificate
          </button>
        </div>
      </li>
    );
  };

  return (
    <section className="audit-portal__panel live-panel">
      <header className="live-panel__header">
        <div>
          <h2>Client review &amp; sign-off</h2>
          <p className="live-muted">
            The client confirms the schedule result here. Each sign-off keeps a snapshot of the
            figures at that moment; signing again replaces the person's earlier decision (the old
            one stays in the history).
          </p>
        </div>
        {isClient ? (
          <button type="button" className="compare-button" onClick={onSignClick}>
            Review &amp; sign off
          </button>
        ) : null}
      </header>
      {error ? <p className="compare-error">{error}</p> : null}
      {rows === null ? (
        <PageLoader label="Loading…" />
      ) : current.length === 0 ? (
        <p className="compare-empty">
          {isClient ? 'You have not signed off this schedule yet.' : 'No client has signed off this schedule yet.'}
        </p>
      ) : (
        <ul className="live-signoffs">{current.map(renderRow)}</ul>
      )}
      {history.length ? (
        <details className="live-history">
          <summary>Earlier sign-offs ({history.length})</summary>
          <ul className="live-signoffs" ref={historyPaged.anchorRef}>{historyPaged.items.map(renderRow)}</ul>
          <Pager paged={historyPaged} noun="sign-offs" compact />
        </details>
      ) : null}
    </section>
  );
}

export { SignoffDialog };
export default SignoffPanel;
